var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Drawer, Grid, IconButton, LinearProgress, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tabs, TextField, Typography } from '@material-ui/core';
import { Close, ExpandMore } from '@material-ui/icons';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { formatDate } from '@sierralabs/core';
import { AppSnackbar, SearchBox, useDebounce, WrappedTableCell } from '@sierralabs/core-ui';
import { defaultQueryBuilderPlugins, SelectQueryBuilder2 } from '@sierralabs/query-ui';
import { useSiteTablePagination } from '../../hooks/site-table/useSiteTablePagination';
import SaveReportDialog from '../../reports/dialogs/SaveReportDialog';
const StyledDiv = styled.div `
  width: 420px;
  display: flex;
  flex-direction: column;
  min-height: min-content;
  flex: 1 1 auto;
  background: #f4f4f4;
  .drawer-header {
    padding: 20px 20px 20px 12px;
  }
  .header-title,
  .header-actions {
    display: flex;
    align-items: center;
  }
  .header-actions:not(:last-child) {
    margin-right: 8px;
  }
  .header-title {
    flex: 1;
    min-height: 31px;
  }
  .content,
  .drawer-actions {
    padding: 0 20px 20px;
  }
  .alert {
    margin-top: 20px;
    .MuiAlertTitle-root {
      font-size: 15px;
      font-weight: 600;
    }
  }
  .search-box {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .select-query-builder-2 {
    margin-bottom: 32px;
  }
  .button {
    margin-top: 16px;
  }
`;
const StyledAccordion = styled(Accordion) `
  margin: 0 !important;
  .MuiAccordionSummary-root {
    max-height: 50px;
    padding-left: 12px;
    padding-right: 12px;
    &.Mui-expanded {
      min-height: unset;
    }
    .summary-text {
      width: 0;
      min-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .MuiAccordionDetails-root {
    padding: 0;
    flex-direction: column;
  }
`;
export const QueryReportFilterDrawer = props => {
    const { open, manager, query: queryProp, aliases, getReportList, getSelectedReport, onCancel, onSearch, onQueryChange, onSelectQuery, onSelectReportIds } = props;
    const mountRef = useRef(false);
    const onSearchRef = useRef(onSearch);
    onSearchRef.current = onSearch;
    const [tab, setTab] = useState('reports');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState(queryProp);
    const [error, setError] = useState();
    const [expanded, setExpanded] = useState(0);
    const [hideAdvanced, setHideAdvanced] = useState(true);
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    // use index to allow getting selected report by index
    const [selectedQueryIndex, setSelectedQueryIndex] = useState(0);
    const debouncedSearch = useDebounce(search, 300);
    const getAlias = (index) => {
        return index > -1 ? aliases[index] : undefined;
    };
    const updateSelectedQuery = (index) => {
        var _a;
        setSelectedQueryIndex(index);
        onSelectQuery === null || onSelectQuery === void 0 ? void 0 : onSelectQuery((_a = getAlias(index)) === null || _a === void 0 ? void 0 : _a.query);
    };
    const updateSelectedReportIds = (applyChanges) => {
        onSelectReportIds === null || onSelectReportIds === void 0 ? void 0 : onSelectReportIds(manager.getSelectedReports().map(report => (report ? report.id : null)), applyChanges);
    };
    const selectedReportItem = useMemo(() => {
        return getSelectedReport(selectedQueryIndex);
    }, [selectedQueryIndex, getSelectedReport]);
    const selectedReport = selectedReportItem === null || selectedReportItem === void 0 ? void 0 : selectedReportItem.data;
    const selectedAlias = getAlias(selectedQueryIndex);
    useEffect(() => {
        setQuery(queryProp);
    }, [queryProp]);
    useEffect(() => {
        selectedReport && setQuery(selectedReport.query);
    }, [selectedReport]);
    useEffect(() => {
        var _a;
        // skip first trigger
        if (!mountRef.current) {
            mountRef.current = true;
            return;
        }
        (_a = onSearchRef.current) === null || _a === void 0 ? void 0 : _a.call(onSearchRef, debouncedSearch);
    }, [debouncedSearch]);
    return (React.createElement(Drawer, { open: open, anchor: 'right', onClose: onCancel },
        React.createElement(StyledDiv, { className: 'drawer-content' },
            React.createElement(Grid, { container: true, className: 'drawer-header' },
                React.createElement(Grid, { item: true, className: 'header-actions' },
                    React.createElement(IconButton, { size: 'small', onClick: onCancel },
                        React.createElement(Close, { fontSize: 'small' }))),
                React.createElement(Grid, { item: true, className: 'header-title' },
                    React.createElement(Typography, { variant: 'h2' }, "Filters")),
                React.createElement(Grid, { item: true, className: 'header-actions' }, tab === 'query' && (React.createElement(Button, { size: 'small', color: 'primary', variant: 'outlined', onClick: () => setHideAdvanced(hideAdvanced => !hideAdvanced) },
                    hideAdvanced ? 'Show' : 'Hide',
                    " Advanced")))),
            React.createElement(Tabs, { value: tab, variant: 'fullWidth', textColor: 'primary', indicatorColor: 'primary', onChange: (_, value) => setTab(value) },
                React.createElement(Tab, { value: 'reports', label: 'Reports' }),
                React.createElement(Tab, { value: 'query', disabled: selectedReportItem === null || selectedReportItem === void 0 ? void 0 : selectedReportItem.isLoading, label: selectedReport ? 'Report Query' : 'Query' })),
            tab === 'reports' ? (React.createElement(React.Fragment, null,
                React.createElement("div", null,
                    React.createElement(SearchBox, { value: search, placeholder: 'Search Report or Creator Name', onSearch: value => setSearch(value) })),
                aliases.map((alias, index) => {
                    const key = `${alias.name}-${index}`;
                    const reportItem = getSelectedReport(index);
                    const table = (React.createElement(ReportsTable, { list: getReportList(index), selected: reportItem, onSelect: (report) => __awaiter(void 0, void 0, void 0, function* () {
                            if (!report) {
                                manager.deselect(index);
                            }
                            else {
                                yield manager.select(index, report.id);
                            }
                            updateSelectedReportIds(false);
                        }), onPaginationChange: params => {
                            manager.load(index, Object.assign(Object.assign({}, params), { aliasName: alias.name }));
                        } }));
                    return aliases.length === 1 ? (React.createElement(Fragment, { key: key }, table)) : (React.createElement(StyledAccordion, { key: key, expanded: index === expanded, onChange: (_, expanded) => {
                            setExpanded(expanded ? index : -1);
                            expanded && updateSelectedQuery(index);
                        } },
                        React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
                            React.createElement(Typography, { className: 'summary-text' }, alias.label || alias.query.name)),
                        React.createElement(AccordionDetails, null, table)));
                }),
                React.createElement("div", { className: 'drawer-actions' },
                    React.createElement(Button, { fullWidth: true, color: 'primary', variant: 'contained', className: 'button', disabled: (selectedReportItem === null || selectedReportItem === void 0 ? void 0 : selectedReportItem.isLoading) || !selectedReport, onClick: () => {
                            selectedReport && (onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(selectedReport.query));
                        } }, "Apply Report")))) : (React.createElement("div", { className: 'content' },
                aliases.length > 1 && (React.createElement(Box, { mt: 2 },
                    React.createElement(Autocomplete, { fullWidth: true, options: aliases, value: selectedAlias || null, getOptionLabel: option => option.label || option.query.name, onChange: (_, value) => {
                            let index = aliases.findIndex(alias => alias.query.id === (value === null || value === void 0 ? void 0 : value.query.id));
                            index = index < 0 ? 0 : index;
                            setExpanded(index);
                            updateSelectedQuery(index);
                        }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Query', variant: 'outlined', placeholder: 'Select Query' }))) }))),
                selectedReport && (React.createElement(Alert, { severity: 'info', className: 'alert' },
                    React.createElement(AlertTitle, null, "Using Report Query"),
                    "Report \"",
                    React.createElement("strong", null, selectedReport.query.name),
                    "\" Query and its filters are currently applied.")),
                React.createElement(SelectQueryBuilder2
                // trigger re-render when selected query changes
                , { 
                    // trigger re-render when selected query changes
                    key: (query === null || query === void 0 ? void 0 : query.id) || '', query: query, hideAdvanced: hideAdvanced, plugins: defaultQueryBuilderPlugins, onChange: query => setQuery(query) }),
                React.createElement(Button, { fullWidth: true, color: 'primary', variant: 'contained', disabled: !query, onClick: () => query && (onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(query)) }, "Apply Filter"),
                React.createElement(Button, { fullWidth: true, color: 'primary', variant: 'outlined', className: 'button', onClick: () => setOpenSaveDialog(true) },
                    selectedReport ? 'Save' : 'Save as',
                    " Report")))),
        openSaveDialog && (React.createElement(SaveReportDialog, { open: true, report: selectedReport, queryConfig: query === null || query === void 0 ? void 0 : query.config, onCancel: () => setOpenSaveDialog(false), onSave: (report) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                setOpenSaveDialog(false);
                const aliasName = (_a = getAlias(selectedQueryIndex)) === null || _a === void 0 ? void 0 : _a.name;
                yield Promise.all([
                    aliasName
                        ? manager.load(selectedQueryIndex, { aliasName })
                        : undefined,
                    manager.select(selectedQueryIndex, report.id)
                ]);
                updateSelectedReportIds(true);
            }) })),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, (error === null || error === void 0 ? void 0 : error.message) || 'An error occurred.')));
};
export default QueryReportFilterDrawer;
const StyledTableContainer = styled(TableContainer) `
  .row-divider {
    background-color: ${props => props.theme.palette.grey[200]};
    .MuiTableCell-root {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
`;
const headers = [
    { key: 'query.name', label: 'Name' },
    { key: 'report.modified', label: 'Last Modified' },
    { key: 'report.createdBy', label: 'Created By' }
];
const ReportsTable = props => {
    const { list, selected, disabled, onSelect, onPaginationChange } = props;
    const { data: reportList } = list || {};
    const { data: selectedReport } = selected || {};
    const isLoading = (list === null || list === void 0 ? void 0 : list.isLoading) || (selected === null || selected === void 0 ? void 0 : selected.isLoading);
    const { pagination, setPagination, isActive, handleSort, getSortDirection } = useSiteTablePagination({ direction: 'ASC' });
    const selectedReportId = selectedReport === null || selectedReport === void 0 ? void 0 : selectedReport.id;
    const filteredReports = useMemo(() => {
        const reports = (reportList === null || reportList === void 0 ? void 0 : reportList.data) || [];
        return selectedReportId
            ? reports.filter(report => report.id !== selectedReportId)
            : reports;
    }, [reportList, selectedReportId]);
    const changePagination = (params) => {
        setPagination(params);
        onPaginationChange === null || onPaginationChange === void 0 ? void 0 : onPaginationChange(Object.assign(Object.assign({}, pagination), params));
    };
    const renderRows = (reports) => {
        return reports.map(report => {
            const isSelected = selectedReportId === report.id;
            return (React.createElement(TableRow, { hover: true, key: report.id, selected: isSelected, onClick: () => {
                    if (!disabled) {
                        onSelect === null || onSelect === void 0 ? void 0 : onSelect(isSelected ? undefined : report);
                    }
                } },
                React.createElement(TableCell, { padding: 'checkbox' },
                    React.createElement(Checkbox, { color: 'primary', checked: isSelected, disabled: disabled })),
                React.createElement(WrappedTableCell, null, report.query.name),
                React.createElement(WrappedTableCell, null, formatDate(report.modified)),
                React.createElement(WrappedTableCell, null,
                    report.createdBy.firstName,
                    " ",
                    report.createdBy.lastName)));
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTableContainer, null,
            React.createElement(Table, { stickyHeader: true },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { padding: 'checkbox' }),
                        headers.map(header => (React.createElement(TableCell, { key: header.key },
                            React.createElement(TableSortLabel, { active: isActive(header.key), direction: getSortDirection(header.key), onClick: () => handleSort(header.key) }, header.label)))))),
                React.createElement(TableBody, null,
                    selectedReport && (React.createElement(React.Fragment, null,
                        React.createElement(TableRow, { className: 'row-divider' },
                            React.createElement(TableCell, { colSpan: 4 },
                                React.createElement(Typography, { variant: 'overline' }, "Selected Report"))),
                        renderRows([selectedReport]),
                        React.createElement(TableRow, { className: 'row-divider' },
                            React.createElement(TableCell, { colSpan: 4 },
                                React.createElement(Typography, { variant: 'overline' }, "Available Reports"))))),
                    isLoading && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: 4, padding: 'none' },
                            React.createElement(LinearProgress, null)))),
                    filteredReports.length === 0 && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: 4 },
                            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' }, isLoading ? 'Loading...' : 'No Reports available.')))),
                    renderRows(filteredReports)))),
        React.createElement(TablePagination, { rowsPerPageOptions: [10, 25, 100], component: 'div', count: (reportList === null || reportList === void 0 ? void 0 : reportList.total) || 0, rowsPerPage: pagination.limit, page: pagination.page, onPageChange: (_, page) => changePagination({ page }), onRowsPerPageChange: event => {
                changePagination({ page: 0, limit: +event.target.value });
            } })));
};
//# sourceMappingURL=QueryReportFilterDrawer.js.map